import * as React from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';

export type CoachChatConfig = Omit<AiCoachRespondToUserMessageInput, 'message'> & { message?: undefined };

type CoachStateProviderType = {
  coachOpen: boolean;
  setCoachOpen: React.Dispatch<React.SetStateAction<boolean>>;
  contextConfig: CoachChatConfig;
  setContextConfig: React.Dispatch<React.SetStateAction<CoachChatConfig>>;
  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  setRenderCustomMessage: React.Dispatch<
    React.SetStateAction<((props: ChatMessageProps) => React.ReactElement | null) | undefined>
  >;
  isMinimizedChat: boolean;
  setIsMinimizedChat: React.Dispatch<React.SetStateAction<boolean>>;
  toggleFullScreen: () => void;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
  setTriggerElementRef: (elm: HTMLElement | null) => void;
  isContextConfigLoading?: boolean;
};
const defaultCoachState: CoachStateProviderType = {
  coachOpen: false,
  setCoachOpen: () => {},
  contextConfig: {
    config: {
      coachActionId: '',
      coachModeId: 'BASIC',
    },
  },
  setContextConfig: () => {},
  renderCustomMessage: undefined,
  setRenderCustomMessage: () => {},
  isMinimizedChat: false,
  setIsMinimizedChat: () => {},
  toggleFullScreen: () => {},
  triggerElementRef: { current: null },
  setTriggerElementRef: () => {},
};

const CoachStateContext = createContext(defaultCoachState);

/**
 * @description This hook is used to access the coach state such as open/close state and context config.
 */
export const useCoachState = () => {
  const context = useContext(CoachStateContext);
  return context;
};

/**
 * @description This provider is used to manage the state of Coach.
 */
export const CoachStateProvider = ({
  children,
  coachOpen: coachOpenProp,
  contextConfig: contextConfigProp,
  isContextConfigLoading,
}: {
  children: React.ReactNode;
  coachOpen?: boolean;
  contextConfig?: CoachChatConfig;
  isContextConfigLoading?: boolean;
}) => {
  const [coachOpen, setCoachOpen] = useState(coachOpenProp ?? defaultCoachState.coachOpen);
  const [contextConfig, setContextConfig] = useState(contextConfigProp ?? defaultCoachState.contextConfig);
  const [isMinimizedChat, setIsMinimizedChat] = useState(defaultCoachState.isMinimizedChat);
  const [renderCustomMessage, setRenderCustomMessage] = useState<
    ((props: ChatMessageProps) => React.ReactElement | null) | undefined
  >(defaultCoachState.renderCustomMessage);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const setTriggerElementRef = (elm: HTMLElement | null) => {
    triggerElementRef.current = elm;
  };

  const toggleFullScreen = useCallback<() => void>(() => setIsMinimizedChat((prev) => !prev), [setIsMinimizedChat]);

  return (
    <CoachStateContext.Provider
      value={{
        coachOpen,
        setCoachOpen,
        isContextConfigLoading,
        contextConfig,
        setContextConfig: (...data) => {
          setContextConfig(...data);
        },
        renderCustomMessage,
        setRenderCustomMessage,
        isMinimizedChat,
        setIsMinimizedChat,
        toggleFullScreen,
        triggerElementRef,
        setTriggerElementRef,
      }}
    >
      {children}
    </CoachStateContext.Provider>
  );
};
